'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { usePostHog } from 'posthog-js/react';
import { resolveChannelLiveVideoId } from '@/lib/livestream/youtubeLive';
import { isValidChannel, sourceKey, type YouTubeSource } from '@/lib/livestream/youtube';
import FloatingLivestreamCard from './FloatingLivestreamCard';

/* =============================================================================
   Floating livestream player — shows whenever the configured YouTube channel
   is actually broadcasting, with no schedule involved.

   Per leadership decision (2026-09-25, see
   docs/specs/2026-09-25-livestream-no-schedule-design.md): the team has no
   operational process to keep a per-event Sanity document up to date, so
   liveness is decided purely by polling YouTube — no calendar, no
   pre-roll/post-roll window. This replaces the schedule-gated design from
   issues #50-#57.

   This component owns:
     1. the channel-resolution poll (issue #51's bot-wall-resistant HTML
        parsing, unchanged),
     2. per-broadcast dismissal,
     3. the context the header pill and the card both read.
============================================================================= */

/**
 * How often the channel is re-asked "are you live yet?". Runs continuously —
 * there is no schedule gate. The server action caches upstream for the same
 * window (youtubeLive.ts's REVALIDATE_SECONDS), so every concurrent viewer
 * collapses into one request per channel per window, server-side.
 */
const CHANNEL_RESOLVE_INTERVAL_MS = 5 * 60 * 1000;

/* ── Dismissal, backed by sessionStorage ──────────────────────────────────────
   Read through useSyncExternalStore rather than mirrored into state, so a
   reload inside the same stream stays dismissed without an effect that has to
   setState on mount.
---------------------------------------------------------------------------- */

const dismissKey = (instanceKey: string) => `livestream-dismissed-${instanceKey}`;

const dismissListeners = new Set<() => void>();

function notifyDismissChange() {
  for (const listener of dismissListeners) listener();
}

function subscribeToDismissals(listener: () => void): () => void {
  dismissListeners.add(listener);
  // `storage` fires in OTHER tabs, so a dismissal follows the user across them.
  window.addEventListener('storage', listener);
  return () => {
    dismissListeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

function readDismissed(instanceKey: string): boolean {
  try {
    return window.sessionStorage.getItem(dismissKey(instanceKey)) === '1';
  } catch {
    // Private mode / storage disabled — treat as not dismissed.
    return false;
  }
}

function writeDismissed(instanceKey: string, dismissed: boolean) {
  try {
    if (dismissed) window.sessionStorage.setItem(dismissKey(instanceKey), '1');
    else window.sessionStorage.removeItem(dismissKey(instanceKey));
  } catch {
    /* ignore */
  }
  notifyDismissChange();
}

/** What the channel is currently broadcasting, resolved and ready to embed. */
interface LiveChannel {
  source: YouTubeSource;
  /** Display label for the card header — youtubeLiveWidget.channelLabel. */
  channelLabel: string;
}

interface LivestreamContextValue {
  /** The channel's current broadcast, or null when nothing is live. */
  live: LiveChannel | null;
  /** True when the card is on screen. */
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const LivestreamContext = createContext<LivestreamContextValue>({
  live: null,
  isOpen: false,
  open: () => {},
  close: () => {},
});

export function useLivestream() {
  return useContext(LivestreamContext);
}

export default function FloatingLivestreamProvider({
  channelLabel,
  liveChannel,
  children,
}: {
  /** youtubeLiveWidget.channelLabel — display label, defaults to "IQ Capital TV". */
  channelLabel?: string;
  /** youtubeLiveWidget.liveChannel — the @handle or UC… id to poll. */
  liveChannel?: string;
  children: React.ReactNode;
}) {
  const posthog = usePostHog();
  const label = channelLabel || 'IQ Capital TV';
  const channel = liveChannel && isValidChannel(liveChannel) ? liveChannel : null;

  /** The resolved broadcast, or null (nothing live, or not resolved yet). */
  const [videoId, setVideoId] = useState<string | null>(null);

  useEffect(() => {
    if (!channel) return;

    let cancelled = false;

    const resolve = async () => {
      const id = await resolveChannelLiveVideoId(channel);
      if (cancelled) return;
      // A `null` read is ambiguous between "confirmed not live" and "couldn't
      // tell" (resolveChannelLiveVideoId fails closed either way — see its
      // own doc comment in lib/livestream/youtubeLive.ts). Keeping the
      // previous id on null would wrongly pin a card open forever once a
      // stream ends, so null always clears. A transient failure clears the
      // card for one cycle rather than getting stuck on a stale video; the
      // next successful poll (≤5 min later) restores it if still live.
      setVideoId(id);
    };

    void resolve();
    const timer = window.setInterval(() => void resolve(), CHANNEL_RESOLVE_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void resolve();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [channel]);

  const live = useMemo<LiveChannel | null>(
    () => (videoId ? { source: { kind: 'video', videoId }, channelLabel: label } : null),
    [videoId, label],
  );

  /**
   * Identifies one broadcast. Built from the resolved videoId (not the
   * channel alone), so dismissing today's stream doesn't silence a different
   * broadcast on the same channel tomorrow.
   */
  const instanceKey = live ? `live:${channel}:${sourceKey(live.source)}` : null;

  // Snapshot is the dismissed instance key (or ''), so it compares by value and
  // never produces a fresh object that would loop useSyncExternalStore.
  const getDismissedSnapshot = useCallback(
    () => (instanceKey && readDismissed(instanceKey) ? instanceKey : ''),
    [instanceKey],
  );
  const dismissedInstance = useSyncExternalStore(subscribeToDismissals, getDismissedSnapshot, () => '');

  const isDismissed = !!instanceKey && dismissedInstance === instanceKey;
  const isOpen = !!live && !isDismissed;

  const close = useCallback(() => {
    if (!instanceKey || !live) return;
    writeDismissed(instanceKey, true);
    posthog?.capture('livestream_player_dismissed', { video_id: sourceKey(live.source) });
  }, [instanceKey, live, posthog]);

  const open = useCallback(() => {
    if (!instanceKey || !live) return;
    writeDismissed(instanceKey, false);
    posthog?.capture('livestream_player_reopened', { video_id: sourceKey(live.source) });
  }, [instanceKey, live, posthog]);

  // One `shown` event per broadcast instance, not per render.
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !instanceKey || !live) return;
    if (shownRef.current === instanceKey) return;
    shownRef.current = instanceKey;
    posthog?.capture('livestream_player_shown', { video_id: sourceKey(live.source) });
  }, [isOpen, instanceKey, live, posthog]);

  // "Why isn't the player showing?" has a couple of legitimate answers. Dev only.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!channel) {
      console.info('[livestream] not shown — no valid liveChannel configured on youtubeLiveWidget');
      return;
    }
    if (!live) console.info('[livestream] not shown — %s is not currently broadcasting', channel);
  }, [channel, live]);

  const value = useMemo<LivestreamContextValue>(() => ({ live, isOpen, open, close }), [live, isOpen, open, close]);

  return (
    <LivestreamContext.Provider value={value}>
      {children}
      <FloatingLivestreamCard />
    </LivestreamContext.Provider>
  );
}
